import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { CardElement } from '@stripe/react-stripe-js';
import useStyles from './styles';
import { FormikProps } from 'formik';
import { CheckoutValues } from 'types/payments';
import { useTheme } from '@mui/material/styles';
import { TextField } from '@elevator/ui';
import CountrySelect from 'atoms/CountrySelect';
import { POSTAL_CODE_MAX_LENGTH } from 'lib/postalCode';

interface CreditCardFormProps
  extends Pick<FormikProps<CheckoutValues>, 'values' | 'errors' | 'touched'> {
  handleInputChange: (fieldName: string, event: any, imperativeValue?: any) => void;
  handleInputBlur: (
    fieldName: string,
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleCardBlur: () => void;
}

type StripeCardChange = {
  complete?: boolean;
  error?: { message?: string };
};

/**
 * CreditCardForm Component
 *
 * Form component for entering credit card payment details using Stripe CardElement.
 * Displays name on card, card input field, country, and postal code.
 * Uses createToken() so the backend receives a token (tok_) for the legacy Sources API.
 */
const CreditCardForm: React.FC<CreditCardFormProps> = ({
  handleInputChange,
  handleInputBlur,
  handleCardBlur,
  values,
  errors,
  touched,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const cardElementOptions = React.useMemo(
    () => ({
      hidePostalCode: true,
      style: {
        base: {
          fontFamily: theme.typography.fontFamily as string,
          fontSize: `${Number(theme.typography.body1?.fontSize) || 16}px`,
          fontWeight: Number(theme.typography.body1.fontWeight) || 400,
          color: theme.palette.text.primary,
          iconColor: theme.palette.text.primary,
          '::placeholder': {
            color: theme.palette.text.secondary,
          },
          ':focus': {
            color: theme.palette.text.primary,
          },
        },
        invalid: {
          color: theme.palette.error.main,
          iconColor: theme.palette.error.main,
        },
        complete: {
          color: theme.palette.text.primary,
          iconColor: theme.palette.text.primary,
        },
      },
    }),
    [
      theme.palette.text.primary,
      theme.palette.text.secondary,
      theme.palette.error.main,
      theme.typography.fontFamily,
      theme.typography.body1?.fontSize,
      theme.typography.body1?.fontWeight,
    ],
  );

  const cardInfo = values.cardInfo as StripeCardChange | undefined;
  const cardErrorMessage =
    cardInfo?.error?.message ??
    (touched.cardInfo && cardInfo && !cardInfo.complete
      ? 'Please enter a valid card'
      : '');

  const gap = 2;

  return (
    <Stack direction="column" gap={gap} width="100%" data-component="CreditCardForm">
      <Box className={classes.textField}>
        <TextField
          id={'nameOnCard'}
          name={'nameOnCard'}
          label={'Cardholder Name'}
          autoComplete={'name'}
          fullWidth
          required
          value={values.nameOnCard}
          onChange={(event) => handleInputChange('nameOnCard', event)}
          onBlur={(event) => handleInputBlur('nameOnCard', event)}
          error={touched.nameOnCard && Boolean(errors.nameOnCard)}
          helperText={touched.nameOnCard ? errors.nameOnCard : ''}
        />
      </Box>
      <Box className={classes.textField}>
        <div className={classes.cardInputContainer}>
          <CardElement
            id={'cardInfo'}
            onChange={(change) => handleInputChange('cardInfo', null, change)}
            onBlur={handleCardBlur}
            options={cardElementOptions}
          />
        </div>
        <Typography variant={'caption'} color={'error'}>
          {cardErrorMessage}
        </Typography>
      </Box>
      <Box className={classes.billingRowGrid}>
        <Box className={classes.billingRowField}>
          <CountrySelect
            value={values.country ?? ''}
            onChange={(event) => handleInputChange('country', event)}
            onBlur={(event) => handleInputBlur('country', event)}
            error={touched.country && Boolean(errors.country)}
            helperText={touched.country ? errors.country : ''}
            required
          />
        </Box>
        <Box className={classes.billingRowField}>
          <TextField
            id={'postalCode'}
            name={'postalCode'}
            label={'ZIP / Postal Code'}
            fullWidth
            required
            value={values.postalCode ?? ''}
            placeholder={'12345'}
            autoComplete={'postal-code'}
            onChange={(event) => handleInputChange('postalCode', event)}
            onBlur={(event) => handleInputBlur('postalCode', event)}
            error={touched.postalCode && Boolean(errors.postalCode)}
            helperText={
              touched.postalCode && errors.postalCode
                ? errors.postalCode
                : 'Letters, numbers, spaces, and hyphens (2–12 characters)'
            }
            slotProps={{
              htmlInput: { maxLength: POSTAL_CODE_MAX_LENGTH },
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};

export default CreditCardForm;
